// frontend/src/components/FeatureMarketplace.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Store, Clock, Cloud, Rss, AlertTriangle, Layout, 
  Check, Lock, Zap, DollarSign, Users, Settings, X, CreditCard  
} from 'lucide-react';

const FeatureMarketplace = () => {
  const { t } = useTranslation();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activatingWidget, setActivatingWidget] = useState(false);

  useEffect(() => {
    fetchFeatures();
  }, []);

  // ✅ HELPER-FUNKTION: Übersetzt Feature-Namen und Beschreibungen
  const getTranslatedFeature = (feature) => {
    const translationKey = `marketplace.features.${feature.id}`;
    
    // Versuche Übersetzung zu laden
    const translatedName = t(`${translationKey}.name`, { defaultValue: '' });
    const translatedDescription = t(`${translationKey}.description`, { defaultValue: '' });
    
    // Wenn Übersetzung vorhanden, nutze sie, sonst Original vom Backend
    return {
      ...feature,
      name: translatedName || feature.name,
      description: translatedDescription || feature.description
    };
  };

  const fetchFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/features`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFeatures(data.features || []);
    } catch (error) {
      console.error('Error fetching features:', error);
      alert(t('marketplace.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleActivateFeature = async (featureId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/features/${featureId}/activate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchFeatures();
        setShowModal(false);
        alert(t('marketplace.activateSuccess'));
      } else {
        const error = await response.json();
        alert(`${t('common.error')}: ${error.error}`);
      }
    } catch (error) {
      console.error('Error activating feature:', error);
      alert(t('marketplace.errors.activateFailed'));
    }
  };

  // 🆕 NEUE FUNKTION: Widget über Stripe aktivieren
  const handleActivateWidget = async (featureId) => {
    setActivatingWidget(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/stripe/create-widget-checkout`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featureId })
      });

      const data = await response.json();
      
      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        alert(`${t('common.error')}: ${data.error || 'Payment checkout failed'}`);
        setActivatingWidget(false);
      }
    } catch (error) {
      console.error('Error creating widget checkout:', error);
      alert(t('marketplace.errors.activateFailed'));
      setActivatingWidget(false);
    }
  };

  const handleDeactivateFeature = async (featureId) => {
    if (!window.confirm(t('marketplace.deactivateConfirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/features/${featureId}/deactivate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchFeatures();
        alert(t('marketplace.deactivateSuccess'));
      }
    } catch (error) {
      console.error('Error deactivating feature:', error);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Clock, Cloud, Rss, AlertTriangle, Layout, Store
    };
    return icons[iconName] || Store;
  };

  const categories = [
  { id: 'all', name: t('marketplace.allFeatures', { defaultValue: 'Alle Features' }), icon: Store },
  { id: 'widgets', name: t('marketplace.widgets', { defaultValue: 'Widgets' }), icon: Zap },
  { id: 'content', name: t('marketplace.content', { defaultValue: 'Inhalt' }), icon: Rss },
  { id: 'system', name: t('marketplace.system', { defaultValue: 'System' }), icon: Settings },
  { id: 'layout', name: t('marketplace.layout', { defaultValue: 'Layout' }), icon: Layout }
];

  const filteredFeatures = selectedCategory === 'all' 
    ? features 
    : features.filter(f => f.category === selectedCategory);

  const FeatureModal = ({ feature: originalFeature, onClose }) => {
    // ✅ Feature übersetzen
    const feature = getTranslatedFeature(originalFeature);
    
    const IconComponent = getIconComponent(feature.icon);
    const isComingSoon = feature.status === 'coming_soon';
    
    // 🆕 Prüfe ob es ein Premium-Widget ist (kostenpflichtig)
    const isPremiumWidget = feature.pricing && 
                           feature.pricing.model === 'subscription' && 
                           feature.pricing.amount > 0;
    
    const isFreeFeature = feature.pricing === 'free' || 
                         feature.pricing?.type === 'free' || 
                         feature.pricing?.amount === 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className={`bg-gradient-to-r ${isComingSoon ? 'from-gray-500 to-gray-700' : isPremiumWidget ? 'from-purple-600 to-purple-800' : 'from-blue-600 to-blue-800'} text-white p-6`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-lg">
                  <IconComponent size={32} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    {feature.name}
                    {isComingSoon && (
                      <span className="bg-yellow-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                        {t('marketplace.comingSoon')}
                      </span>
                    )}
                    {/* 🆕 Premium Badge */}
                    {isPremiumWidget && !feature.isActivated && (
                      <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-sm px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <CreditCard size={14} />
                        Premium
                      </span>
                    )}
                  </h2>
                  <p className="text-blue-100 mt-1">{feature.description}</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded p-1"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Pricing */}
            <div className={`${isPremiumWidget ? 'bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-2 border-purple-200 dark:border-purple-700' : 'bg-gray-50 dark:bg-gray-700'} rounded-lg p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('marketplace.pricing')}
                  </h3>
                  {isFreeFeature ? (
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                      {t('marketplace.free')}
                    </p>
                  ) : feature.pricing === 'paid' ? (
                    <p className="text-xl text-gray-600 dark:text-gray-400 mt-1">
                      {t('marketplace.pricingTBA')}
                    </p>
                  ) : isPremiumWidget ? (
                    <div className="mt-1">
                      <span className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                        {feature.pricing.currency} {feature.pricing.amount}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 ml-2">
                        / {t('marketplace.monthly')}
                      </span>
                    </div>
                  ) : null}
                </div>
                {isPremiumWidget ? (
                  <CreditCard size={32} className="text-purple-400 dark:text-purple-500" />
                ) : (
                  <DollarSign size={32} className="text-gray-400 dark:text-gray-500" />
                )}
              </div>
              
              {/* 🆕 Premium Info Box */}
              {isPremiumWidget && !feature.isActivated && (
                <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-700">
                  <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Sichere Zahlung über Stripe</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mt-1">
                    <Check size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Jederzeit kündbar</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mt-1">
                    <Check size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Monatliche Abrechnung</span>
                  </div>
                </div>
              )}
            </div>

            {isComingSoon && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <p className="text-yellow-800 dark:text-yellow-300 font-medium">
                  🚧 {t('marketplace.comingSoonMessage')}
                </p>
              </div>
            )}

            {/* Limits */}
            {feature.limits && feature.limits.maxInstances && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Users size={18} />
                  {t('marketplace.usageLimits')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {t('marketplace.maximumInstances', { count: feature.limits.maxInstances })}
                </p>
              </div>
            )}

            {/* Configuration Options */}
            {feature.configSchema && Object.keys(feature.configSchema).length > 0 && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <Settings size={18} />
                  {t('marketplace.configurationOptions')}
                </h3>
                <div className="space-y-2">
                  {Object.entries(feature.configSchema).map(([key, config]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300 capitalize">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t(`marketplace.${config.type}`, { defaultValue: config.type })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features List */}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                {t('marketplace.whatsIncluded')}
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('marketplace.easyConfiguration')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('marketplace.realTimeUpdates')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('marketplace.responsiveDesign')}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('marketplace.playlistIntegration')}
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              {!isComingSoon && !feature.isActivated ? (
                <>
                  {/* 🆕 Unterschiedliche Buttons für Premium vs Free */}
                  {isPremiumWidget ? (
                    <button
                      onClick={() => handleActivateWidget(feature.id)}
                      disabled={activatingWidget}
                      className={`flex-1 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all flex items-center justify-center gap-2 ${activatingWidget ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {activatingWidget ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Wird geladen...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard size={20} />
                          <span>Widget für CHF {feature.pricing.amount}/Monat aktivieren</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivateFeature(feature.id)}
                      className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                    >
                      {t('marketplace.activateFeature')}
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </>
              ) : isComingSoon ? (
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.close')}
                </button>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                  {feature.canActivate && (
                    <button
                      onClick={() => handleDeactivateFeature(feature.id)}
                      className="px-6 py-3 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      {t('marketplace.deactivate')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Store size={32} className="text-primary-600 dark:text-primary-400" />
          {t('marketplace.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('marketplace.extendPlatform')}
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {categories.map(category => {
          const Icon = category.icon;
          const isActive = selectedCategory === category.id;
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                isActive 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-primary-500'
              }`}
            >
              <Icon size={18} />
              {category.name}
            </button>
          );
        })}
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFeatures.map(originalFeature => {
          // ✅ Feature übersetzen
          const feature = getTranslatedFeature(originalFeature);
          
          const IconComponent = getIconComponent(feature.icon);
          const isComingSoon = feature.status === 'coming_soon';
          
          // 🆕 Prüfe Premium Status
          const isPremiumWidget = feature.pricing && 
                                 feature.pricing.model === 'subscription' && 
                                 feature.pricing.amount > 0;
          
          return (
            <div 
              key={feature.id}
              className={`bg-white dark:bg-gray-800 border ${isPremiumWidget && !feature.isActivated ? 'border-purple-300 dark:border-purple-700' : 'border-gray-200 dark:border-gray-700'} rounded-lg p-6 transition-all ${
                isComingSoon 
                  ? 'opacity-60 cursor-not-allowed' 
                  : 'hover:shadow-lg cursor-pointer'
              }`}
              onClick={() => {
                if (!isComingSoon) {
                  setSelectedFeature(originalFeature);
                  setShowModal(true);
                }
              }}
            >
              {/* Feature Header */}
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${isComingSoon ? 'bg-gray-100 dark:bg-gray-700' : isPremiumWidget ? 'bg-purple-100 dark:bg-purple-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
                  <IconComponent size={24} className={isComingSoon ? 'text-gray-400' : isPremiumWidget ? 'text-purple-600 dark:text-purple-400' : 'text-primary-600 dark:text-primary-400'} />
                </div>
                {isComingSoon ? (
                  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 px-3 py-1 rounded-full text-sm font-medium">
                    {t('marketplace.comingSoon')}
                  </span>
                ) : feature.isActivated ? (
                  <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <Check size={14} />
                    {t('marketplace.active')}
                  </span>
                ) : isPremiumWidget ? (
                  <span className="bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <CreditCard size={14} />
                    Premium
                  </span>
                ) : feature.limits && feature.limits.requiresActivation ? (
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <Lock size={14} />
                    {t('marketplace.inactive')}
                  </span>
                ) : null}
              </div>

              {/* Feature Info */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{feature.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{feature.description}</p>

              {/* Pricing */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                {feature.pricing === 'free' || feature.pricing?.type === 'free' || feature.pricing?.amount === 0 ? (
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    {t('marketplace.free')}
                  </span>
                ) : feature.pricing === 'paid' ? (
                  <span className="text-gray-500 dark:text-gray-400 font-medium">
                    {t('marketplace.pricingTBA')}
                  </span>
                ) : isPremiumWidget ? (
                  <span className="text-purple-600 dark:text-purple-400 font-bold">
                    {feature.pricing.currency} {feature.pricing.amount}
                    <span className="text-gray-500 dark:text-gray-400 font-normal text-sm ml-1">
                      / {t('marketplace.monthly')}
                    </span>
                  </span>
                ) : null}

                {!isComingSoon && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFeature(originalFeature);
                      setShowModal(true);
                    }}
                    className="text-primary-600 dark:text-primary-400 font-medium hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    {t('marketplace.viewDetails')} →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredFeatures.length === 0 && (
        <div className="text-center py-12">
          <Store size={48} className="mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('marketplace.noFeaturesFound')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t('marketplace.tryDifferentCategory')}
          </p>
        </div>
      )}

      {/* Feature Detail Modal */}
      {showModal && selectedFeature && (
        <FeatureModal 
          feature={selectedFeature} 
          onClose={() => {
            setShowModal(false);
            setSelectedFeature(null);
          }}
        />
      )}
    </div>
  );
};

export default FeatureMarketplace;